import React, { forwardRef, useRef } from "react";
import ReactDatePicker from "react-datepicker";
import { CalendarIcon } from "../icons/CalendarIcon";

interface DatePickerProps {
  value: string; // ISO date string (YYYY-MM-DD)
  onChange: (value: string) => void;
  minDate?: string; // ISO date string
  maxDate?: string; // ISO date string
  className?: string;
}

// Custom input component that matches existing styling with calendar icon
const CustomInput = forwardRef<
  HTMLInputElement,
  {
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onCalendarClick?: () => void;
  }
>(({ value, onChange, onCalendarClick }, ref) => (
  <div className="relative">
    <input
      type="text"
      value={value}
      onChange={onChange}
      ref={ref}
      placeholder="MM/DD/YYYY"
      className="border h-10 border-border-gray bg-white pl-3 pr-10 py-1.5 text-sm focus:outline-none focus:border-primary-blue w-full cursor-text"
    />
    <button
      type="button"
      onClick={onCalendarClick}
      className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center text-gray-600 hover:text-primary-blue cursor-pointer"
      tabIndex={-1}
    >
      <CalendarIcon className="w-4 h-4" />
    </button>
  </div>
));

CustomInput.displayName = "CustomInput";

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  className,
}) => {
  const datePickerRef = useRef<ReactDatePicker>(null);
  const parseISODate = (isoString: string): Date | null => {
    if (!isoString) return null;
    const date = new Date(isoString + "T00:00:00");
    return isNaN(date.getTime()) ? null : date;
  };

  const formatToISO = (date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const selectedDate = parseISODate(value);
  const minDateObj = minDate ? (parseISODate(minDate) ?? undefined) : undefined;
  const maxDateObj = maxDate ? (parseISODate(maxDate) ?? undefined) : undefined;

  const handleCalendarClick = () => {
    datePickerRef.current?.setOpen(true);
  };

  return (
    <div className={className}>
      <ReactDatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={(date) => onChange(formatToISO(date))}
        minDate={minDateObj}
        maxDate={maxDateObj}
        dateFormat="MM/dd/yyyy"
        customInput={<CustomInput onCalendarClick={handleCalendarClick} />}
        showPopperArrow={false}
        locale="en-US"
        calendarStartDay={0}
        shouldCloseOnSelect={true}
        onClickOutside={() => datePickerRef.current?.setOpen(false)}
        popperProps={{ strategy: "fixed" }}
        popperClassName="!z-[9999]"
      />
    </div>
  );
};
